/**
 * /app/$tenantSlug/tasks — Inbox → Tasks (nav-build spec C; docs/71 §9:
 * "Tasks holds an employee's own to-dos, so the Inbox isn't only for
 * approvers").
 *
 * One list of what the signed-in person has to do, built from existing
 * endpoints (hooks/use-my-tasks.ts → lib/inbox-tasks.ts, read through
 * hooks/use-inbox-count.ts so the list and the Inbox count agree): exit tasks HR
 * gave them, onboarding steps they own, surveys to answer, sent-back
 * expense claims, missed attendance days, the POSH course (unfinished or
 * due for its refresher), the POSH policy to acknowledge and a self-review
 * to write. Each row opens the owning page. ?type= narrows the list
 * (/exit-tasks redirects to ?type=exit).
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { CheckCircle2 } from "lucide-react";
import { useInbox } from "@/hooks/use-inbox-count";
import {
  INBOX_TASK_KINDS,
  INBOX_TASK_KIND_LABELS,
  presentKinds,
  type InboxTaskKind,
} from "@/lib/inbox-tasks";
import { useTenantSlug } from "@/lib/use-tenant-slug";
import { InboxPage } from "@/components/inbox/inbox-page";
import { QueueList } from "@/components/inbox/queue-row";
import { TaskRow } from "@/components/inbox/task-row";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const searchSchema = z.object({
  type: z.enum(INBOX_TASK_KINDS).optional().catch(undefined),
});

export const Route = createFileRoute("/_authgated/app/$tenantSlug/tasks")({
  validateSearch: searchSchema,
  component: TasksRoute,
});

const ALL = "all";

function TasksRoute() {
  const tenantSlug = useTenantSlug();
  const navigate = useNavigate();
  const { type } = Route.useSearch();
  // The same list the Inbox (n) count is built from (app shell).
  const { tasks, isLoading, failed } = useInbox().tasks;

  const kinds = presentKinds(tasks);
  if (type && !kinds.includes(type)) kinds.push(type);
  const shown = type ? tasks.filter((t) => t.kind === type) : tasks;

  function setType(next: InboxTaskKind | undefined) {
    void navigate({
      to: "/app/$tenantSlug/tasks",
      params: { tenantSlug },
      search: next ? { type: next } : {},
      replace: true,
    });
  }

  return (
    <InboxPage title="Tasks" description="Things waiting for you to do, most urgent first.">
      <div className="space-y-4">
        {failed.length > 0 && (
          <Alert variant="warning">
            <AlertTitle>Some Tasks May Be Missing</AlertTitle>
            <AlertDescription>
              Couldn&apos;t load: {failed.join(", ")}. Reload the page to try again.
            </AlertDescription>
          </Alert>
        )}

        {(kinds.length > 1 || type) && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {shown.length} {shown.length === 1 ? "task" : "tasks"}
            </p>
            <Select
              value={type ?? ALL}
              onValueChange={(v) => setType(v === ALL ? undefined : (v as InboxTaskKind))}
            >
              <SelectTrigger className="w-full sm:w-52" aria-label="Show">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All Tasks</SelectItem>
                {kinds.map((k) => (
                  <SelectItem key={k} value={k}>
                    {INBOX_TASK_KIND_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {isLoading && shown.length === 0 ? (
          <div className="space-y-2" aria-hidden>
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
        ) : shown.length === 0 ? (
          type ? (
            <EmptyState
              icon={CheckCircle2}
              title={`No ${INBOX_TASK_KIND_LABELS[type]} to Do`}
              description="Nothing of this kind is waiting for you."
              action={
                <Button variant="outline" onClick={() => setType(undefined)}>
                  Show All Tasks
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={CheckCircle2}
              title="You're All Caught Up"
              description="When something needs you — a survey, a claim to fix, a missed punch, a course to finish — it shows up here."
            />
          )
        ) : (
          <QueueList label="Your Tasks">
            {shown.map((t) => (
              <TaskRow key={t.id} task={t} />
            ))}
          </QueueList>
        )}
      </div>
    </InboxPage>
  );
}
